/**
 * PR-D4-036 — App root (router) with route-gated session shell.
 * Inactivity auto-logout on /dashboard + /student-dashboard only;
 * bypassed on active test routes (/test, /math-test).
 */

import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import type { ReactNode } from 'react';
import { LandingPage } from './pages/LandingPage';
import { Dashboard } from './pages/Dashboard';
import { TestPage } from './pages/TestPage';
import { ResultsPage } from './pages/ResultsPage';
import { StudentDashboard } from './pages/StudentDashboard';
import { MathTestPage } from './pages/MathTestPage';
import { ReviewMistakesPage } from './pages/ReviewMistakesPage';
import { ReviewSessionPage } from './pages/ReviewSessionPage';
import { useInactivityTimer } from './hooks/useInactivityTimer';
import { handleLogout, isAuthenticated } from './lib/auth-session';
import './madcrab/App.css';

const IDLE_TIMEOUT_MS = 60 * 60 * 1000;

const SESSION_ACTIVE_PATHS = new Set(['/dashboard', '/student-dashboard', '/review-mistakes', '/review-session']);
const TEST_BYPASS_PATHS = new Set(['/test', '/math-test']);

function RequireAuth({ children }: { children: ReactNode }) {
    if (!isAuthenticated()) {
        return <Navigate to="/" replace />;
    }
    return <>{children}</>;
}

function SessionShell({ children }: { children: ReactNode }) {
    const { pathname } = useLocation();
    const idleEnabled =
        SESSION_ACTIVE_PATHS.has(pathname) && !TEST_BYPASS_PATHS.has(pathname);

    useInactivityTimer({
        timeoutMs: IDLE_TIMEOUT_MS,
        enabled: idleEnabled,
        onTimeout: handleLogout,
    });

    return <>{children}</>;
}

export function App() {
    return (
        <BrowserRouter>
            <SessionShell>
                <Routes>
                    <Route path="/" element={<LandingPage />} />
                    <Route
                        path="/dashboard"
                        element={
                            <RequireAuth>
                                <Dashboard />
                            </RequireAuth>
                        }
                    />
                    <Route path="/test" element={<TestPage />} />
                    <Route path="/results" element={<ResultsPage />} />
                    <Route path="/math-test" element={<MathTestPage />} />
                    <Route
                        path="/review-mistakes"
                        element={
                            <RequireAuth>
                                <ReviewMistakesPage />
                            </RequireAuth>
                        }
                    />
                    <Route
                        path="/review-session"
                        element={
                            <RequireAuth>
                                <ReviewSessionPage />
                            </RequireAuth>
                        }
                    />
                    <Route
                        path="/student-dashboard"
                        element={
                            <RequireAuth>
                                <StudentDashboard />
                            </RequireAuth>
                        }
                    />
                    <Route
                        path="*"
                        element={
                            <div className="app-shell app-shell--hub">
                                <div className="app-hub-card">
                                    <p className="app-hub-brand">SAT Generator</p>
                                    <h1 className="app-hub-title">404</h1>
                                    <p className="app-hub-hint">Page not found</p>
                                    <a
                                        href="/dashboard"
                                        className="app-hub-primary"
                                        style={{ textAlign: 'center', textDecoration: 'none' }}
                                    >
                                        Return to Dashboard
                                    </a>
                                </div>
                            </div>
                        }
                    />
                </Routes>
            </SessionShell>
        </BrowserRouter>
    );
}
